import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  bookings,
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * The reminder to everyone who booked. A test to an address that booked
 * shows that guest's exact email; any other address gets sample details.
 */
export function ReminderPanel({
  eventSlug,
  audienceCount,
  bookingCount,
  remindedCount,
  lastSentOn,
  defaultTestEmail,
  senderName,
}: {
  eventSlug: string;
  /** Addresses that booked, walk-ins included. */
  audienceCount: number;
  bookingCount: number;
  /** Bookings already stamped with a reminder. */
  remindedCount: number;
  /** When the last reminder went out ("12 Sep, 7:40 pm"), if logged. */
  lastSentOn: string | null;
  defaultTestEmail: string;
  senderName: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const reminder = trpc.adminSendEventReminder.useMutation();

  const send = (mode: "test" | "all") => {
    reminder.mutate(
      {
        eventSlug,
        mode,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : res.matchedRegistrant
                  ? `Test sent to ${res.sentTo}. It shows exactly what ${res.matchedRegistrant} will get. Open it, check it, then send to everyone.`
                  : `Test sent to ${res.sentTo}. That address hasn't booked, so it uses sample details. Open it, check it, then send to everyone.`,
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "No one who booked has an email address, so nothing was sent.",
              ),
            );
            // The guest list and the event counts read the reminder
            // stamps this send just wrote; the history gains a row.
            void utils.adminListEventRegistrations.invalidate();
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const alreadyReminded =
    remindedCount > 0
      ? `${remindedCount} of ${bookings(bookingCount)} already had a reminder${
          lastSentOn ? ` on ${lastSentOn}` : ""
        }. If you send it now, all ${bookings(bookingCount)} get it, including those ${remindedCount}.`
      : null;

  return (
    <>
      <p className={BODY_TEXT}>
        A friendly reminder to everyone who has booked, with their reference
        number. Send yourself a test first: it is the same email they will get.
      </p>
      {alreadyReminded ? (
        <p className="mt-2 text-[15px] font-semibold text-[color:var(--color-amber)]">
          {alreadyReminded}
        </p>
      ) : null}
      <TestThenSend
        flow={flow}
        emailName="Reminder"
        audience={audienceCount > 0 ? people(audienceCount) : null}
        noAudienceText="No one who booked has an email address yet, so there's no one to send this to."
        busy={reminder.isPending}
        onSendTest={() => send("test")}
        onSendAll={() => send("all")}
        alreadySentNote={alreadyReminded}
      />
    </>
  );
}
